import {
  EVENT_SHELF_CHANGE,
  EVENT_ITEMS_PURCHASED,
  EVENT_RECEIPT_INPUT,
  EVENT_LOYALTY,
  EVENT_ACTIVITY_DETECT,
  EVENT_PERSON_EXIT,
  EVENT_PERSON_SCAN,
} from "../../../Config"
import { UNASSIGNED_CART } from "../../../utils/order"

type EligibleEventCreationTypes =
  | typeof EVENT_ITEMS_PURCHASED
  | typeof EVENT_SHELF_CHANGE
  | typeof EVENT_PERSON_EXIT
  | typeof EVENT_RECEIPT_INPUT
  | typeof EVENT_LOYALTY

export interface ManualEvent {
  cam?: string
  ts?: string
  type?: EligibleEventCreationTypes
  cartId?: string
  details?: any
}

export interface OptionType {
  label: string
  value: string
}

export interface ShelfProductSelectOptionType {
  label: string
  value: string
  imageURL?: string
  id?: string
  name?: string
  shelfId?: string
}

export enum ManualShelfEventTypes {
  TYPE_PICK = "pick",
  TYPE_PUT = "put",
}

export type CommonObject = { [x: string]: string }

export const EVENT_ADD = "event_add"
export const EVENT_REMOVE = "event_remove"
export const EVENT_EDIT = "event_edit"
export const EVENT_MAP = "event_map"

export const EVENT_TYPE_LABELS: Record<string, string> = {
  [EVENT_ITEMS_PURCHASED]: "Items Purchased",
  [EVENT_SHELF_CHANGE]: "Shelf Change",
  [EVENT_PERSON_EXIT]: "Person Exit",
  [EVENT_RECEIPT_INPUT]: "Receipt Input",
  [EVENT_LOYALTY]: "Loyalty",
}

export const ELIGIBLE_EVENT_TYPES_FOR_CREATION = [
  EVENT_ITEMS_PURCHASED,
  EVENT_SHELF_CHANGE,
  EVENT_PERSON_EXIT,
  EVENT_RECEIPT_INPUT,
  EVENT_LOYALTY,
]
export const ELIGIBLE_EVENT_TYPES_FOR_REMOVAL = [
  EVENT_ITEMS_PURCHASED,
  EVENT_SHELF_CHANGE,
  EVENT_RECEIPT_INPUT,
  EVENT_LOYALTY,
  EVENT_ACTIVITY_DETECT,
]
export const ELIGIBLE_EVENT_TYPES_FOR_EDITING = [
  EVENT_ITEMS_PURCHASED,
  EVENT_SHELF_CHANGE,
  EVENT_ACTIVITY_DETECT,
  EVENT_PERSON_EXIT,
]
export const ELIGIBLE_EVENT_TYPES_FOR_MAPPING = [
  EVENT_ITEMS_PURCHASED,
  EVENT_SHELF_CHANGE,
  EVENT_RECEIPT_INPUT,
  EVENT_LOYALTY,
  EVENT_ACTIVITY_DETECT,
]
export const ShelfTypeOptions = [
  { label: ManualShelfEventTypes.TYPE_PICK, value: ManualShelfEventTypes.TYPE_PICK },
  { label: ManualShelfEventTypes.TYPE_PUT, value: ManualShelfEventTypes.TYPE_PUT },
]

export const getEventPrefix = (eventType: string) => {
  switch (eventType) {
    case EVENT_SHELF_CHANGE:
      return `esh`
    case EVENT_ITEMS_PURCHASED:
      return `eip`
    case EVENT_PERSON_EXIT:
      return `epo`
    case EVENT_RECEIPT_INPUT:
      return `eri`
    case EVENT_LOYALTY:
      return `ely`
  }
}

export const isValidTimestamp = (timestamp: number | string) => {
  return timestamp && String(timestamp).length === 13
}

/**
 * Validates if a timestamp is invalid for exit event updates
 * @param timestamp - The timestamp to validate
 * @param startTime - The order start time (timestamp must be >= startTime)
 * @param currentExitTimestamp - The current exit timestamp (timestamp must differ from this)
 * @returns true if timestamp is invalid, false if valid
 */
export const isInvalidExitTimestamp = (
  timestamp: string,
  startTime: string,
  currentExitTimestamp?: string
): boolean => {
  // Check if timestamp is empty
  if (!timestamp) {
    return true
  }

  // Check if timestamp is a valid number
  const timestampNum = Number(timestamp)
  if (isNaN(timestampNum)) {
    return true
  }

  // Check if timestamp is a valid epoch timestamp in milliseconds
  // Must be 13 digits (not in seconds which would be 10 digits)
  const timestampStr = String(timestampNum)
  if (timestampStr.length !== 13) {
    return true
  }

  // Check if timestamp is not less than start time
  if (timestampNum < Number(startTime)) {
    return true
  }

  // Check if timestamp is the same as current exit timestamp
  if (currentExitTimestamp && timestampNum === Number(currentExitTimestamp)) {
    return true
  }

  return false
}

export const getEventComparableTime = (event: any) => {
  if (event?.type === EVENT_PERSON_SCAN) {
    return Number(event?.scan_time || event?.start)
  }
  return Number(event?.start)
}

/**
 * Parse the events in task details
 */
export const parseCartEventsEdits = (cartEventsEdits: unknown): { [x: string]: any } => {
  if (!cartEventsEdits) {
    return {}
  }
  if (typeof cartEventsEdits === "string") {
    try {
      return JSON.parse(cartEventsEdits) || {}
    } catch (err) {
      console.error(`parseCartEventsEdits: Failed to parse cart_events_edits`, err)
      return {}
    }
  }
  if (typeof cartEventsEdits === "object") {
    return cartEventsEdits as { [x: string]: any }
  }
  return {}
}

/**
 * get events from task.cart_events_edits
 */
export const getTaskDetailsEvents = (task: { [x: string]: any }): { [x: string]: any }[] => {
  const cartEventsEdits = parseCartEventsEdits(task?.cart_events_edits)
  if (Object.keys(cartEventsEdits).length === 0) {
    return []
  }
  return Object.entries(cartEventsEdits).map(([eventId, event]) => {
    const { event_snapshot, ...editDetails } = event
    const formattedEvent = { ...editDetails, ...event_snapshot, event_id: eventId }
    if (event?.edit_type === EVENT_REMOVE) {
      formattedEvent.cart = UNASSIGNED_CART
      formattedEvent.resolved_track = UNASSIGNED_CART
    }
    return formattedEvent
  })
}

/**
 * Formats an events-by-id map (as returned by getEventsByTime/getOrderEvents) into a
 * flat array with event_id set on each entry.
 */
export const getFormattedOrderEvents = (
  events: { [x: string]: any } | undefined | null
): { [x: string]: any }[] => {
  if (!events || Object.keys(events).length === 0) return []
  return Object.entries(events).map(([eventId, event]) => ({
    ...event,
    event_id: eventId,
  }))
}

export const formatAndSortEvents = (events: { [x: string]: any }): { [x: string]: any }[] => {
  return getFormattedOrderEvents(events).sort(
    (event1, event2) => getEventComparableTime(event1) - getEventComparableTime(event2)
  )
}

export const handleEventTypesFilter = (
  events: { [x: string]: any }[],
  eventTypes: string[]
): { [x: string]: any }[] => {
  if (Array.isArray(eventTypes) && eventTypes.length > 0) {
    return events.filter((event) => eventTypes.includes(event.type))
  }
  return events
}

export const mergeEvents = (
  newEvents: { [x: string]: unknown }[],
  existingEvents: { [x: string]: unknown }[]
) => {
  const existingEventsIds = new Set(existingEvents.map((event) => event.event_id))
  const filteredNewEvents = newEvents.filter((event) => !existingEventsIds.has(event.event_id))
  if (filteredNewEvents.length > 0) {
    const mergedEvents = existingEvents.concat(filteredNewEvents)
    sortEventsByTimestamp(mergedEvents)

    return mergedEvents
  }
  return existingEvents
}

/**
 * Merges two event lists by event_id.
 *
 * For events present in both lists, fields from primaryEvents take precedence,
 * while fields missing from the primary event are preserved from secondaryEvents.
 * Events present only in secondaryEvents are also included.
 *
 * Used to overlay persisted task-detail event edits onto freshly fetched events.
 */
export const mergeEventsPreferringPrimary = (
  primaryEvents: { [x: string]: any }[],
  secondaryEvents: { [x: string]: any }[]
) => {
  const secondaryEventsById: { [x: string]: any } = {}
  secondaryEvents.forEach((event) => {
    secondaryEventsById[event.event_id] = event
  })
  const primaryEventIds = new Set(primaryEvents.map((event) => event.event_id))
  const mergedEvents = primaryEvents.map((event) => {
    const secondaryEvent = secondaryEventsById[event.event_id]
    return secondaryEvent ? { ...secondaryEvent, ...event } : event
  })
  secondaryEvents.forEach((event) => {
    if (!primaryEventIds.has(event.event_id)) {
      mergedEvents.push(event)
    }
  })
  sortEventsByTimestamp(mergedEvents)
  return mergedEvents
}

export const areNewEventsPresent = (
  newEvents: { [x: string]: unknown }[],
  existingEvents: { [x: string]: unknown }[]
) => {
  const existingEventsIds = new Set(existingEvents.map((event) => event.event_id))
  const filteredNewEvents = newEvents.filter((event) => !existingEventsIds.has(event.event_id))
  return filteredNewEvents.length > 0
}

export const getEventDisplayName = (eventType: string) => {
  switch (eventType) {
    case EVENT_SHELF_CHANGE:
      return "Shelf"
    case EVENT_ITEMS_PURCHASED:
      return "Items Purchased"
    case EVENT_PERSON_EXIT:
      return `Exit`
  }
}

export const sortEventsByTimestamp = (events: { [x: string]: unknown }[]) => {
  events.sort((e1, e2) => {
    const timeA = getEventComparableTime(e1)
    const timeB = getEventComparableTime(e2)

    // Handle cases where timestamp might be undefined
    if (timeA === undefined && timeB === undefined) return 0
    if (timeA === undefined) return 1
    if (timeB === undefined) return -1

    return timeA - timeB
  })
}

export const getLargestTimeUnit = (timestamp: number): string => {
  if (timestamp < 0) return ""

  const hours = Math.floor(timestamp / (1000 * 60 * 60))
  if (hours > 0) return `${hours}h`

  const minutes = Math.floor(timestamp / (1000 * 60))
  if (minutes > 0) return `${minutes}m`

  const seconds = Math.floor(timestamp / 1000)
  if (seconds > 0) return `${seconds}s`

  return `${timestamp}ms`
}

/**
 * Returns the cameras directly adjacent (up/down/left/right, plus their own
 * neighbours) to `cam` per the store's camera navigation graph.
 */
export const getAdjacentCamsOfCamera = (
  cam: string,
  cameraNavigationList: { [x: string]: any }
): string[] => {
  const immediateNeighbourCams = cameraNavigationList[cam]
  if (!immediateNeighbourCams) {
    return []
  }
  const res: string[] = Object.values(immediateNeighbourCams)
  const upCam = immediateNeighbourCams["up"]
  if (upCam) {
    const upCamNeighbours = cameraNavigationList[upCam]
    res.push(upCamNeighbours["right"], upCamNeighbours["left"])
  }
  const downCam = immediateNeighbourCams["down"]
  if (downCam) {
    const downCamNeighbours = cameraNavigationList[downCam]
    res.push(downCamNeighbours["right"], downCamNeighbours["left"])
  }
  const leftCam = immediateNeighbourCams["left"]
  if (leftCam) {
    const leftCamNeighbours = cameraNavigationList[leftCam]
    res.push(leftCamNeighbours["up"], leftCamNeighbours["down"])
  }
  const rightCam = immediateNeighbourCams["right"]
  if (rightCam) {
    const rightCamNeighbours = cameraNavigationList[rightCam]
    res.push(rightCamNeighbours["up"], rightCamNeighbours["down"])
  }
  const cams = new Set(res)
  cams.delete((undefined as unknown) as string)
  return Array.from(cams)
}

/**
 * Orders candidate cameras to check between cam1 and cam2 when looking for a
 * checkpoint between two events: cameras adjacent to both first, then the rest.
 */
export const getCameraIds = (
  cam1: string,
  cam2: string,
  cameraNavigationList: { [x: string]: any }
): string[] => {
  const adjacentCamsOfCam1 = getAdjacentCamsOfCamera(cam1, cameraNavigationList)
  const adjacentCamsOfCam2 = getAdjacentCamsOfCamera(cam2, cameraNavigationList)
  const commonCams: string[] = []
  const otherCams: string[] = []
  for (const cam of adjacentCamsOfCam1) {
    if (adjacentCamsOfCam2.includes(cam)) {
      commonCams.push(cam)
    } else {
      otherCams.push(cam)
    }
  }
  return Array.from(new Set([cam1, cam2, ...commonCams, ...otherCams]))
}

/**
 * Binary search for the index of the smallest timestamp in `timestamps` (as strings,
 * e.g. object keys) that is greater than currentTimestamp; -1 if none is.
 */
export const getNextHighestTimestampIndex = (
  timestamps: string[],
  currentTimestamp: number
): number => {
  let low = 0
  let high = timestamps.length - 1
  let idx = -1
  while (low <= high) {
    const mid = Math.floor((low + high) / 2)
    if (Number(timestamps[mid]) > currentTimestamp) {
      idx = mid
      high = mid - 1
    } else {
      low = mid + 1
    }
  }
  return idx
}

/**
 * Checks if the specified cartId is present in the order tracks of a camera at the
 * calculated mid timestamp. Order tracks might not exist precisely at the given
 * timestamp, so nearby timestamps are also examined.
 *
 * @param cartId - orderId/cartId which needs to be checked
 * @param timestamp - timestamp around which order tracks needs to be checked
 * @param orderTracks - order tracks of camera between cart entry and exit timestamps
 * @param startTs - start time of timestamps between which check point is introduced
 * @param endTs - end time of timestamps between which check point is introduced
 * @returns nearest timestamp of given timestamp in which order track is present
 */
export const findTimestampWithOrderTrack = (
  cartId: string,
  timestamp: number,
  orderTracks: { [x: string]: any },
  startTs: number,
  endTs: number
): string | null => {
  if (orderTracks) {
    const timestamps = Object.keys(orderTracks) // timestamps for which order tracks are available
    const idx = getNextHighestTimestampIndex(timestamps, timestamp) // get the next immediate highest timestamp from the list of timestamps
    const nearestTimestamps = timestamps.slice(idx - 2, idx + 2) // get list of nearest timestamps for which order tracks are available
    // check if the cartId is present in order tracks of any nearest timestamps
    for (const ts of nearestTimestamps) {
      if (Number(ts) < startTs || Number(ts) > endTs) {
        // If the timestamp (ts) does not fall within the event timestamps between which a checkpoint is introduced, then ignore it and continue with next timestamp.
        continue
      }
      const trackDetectsOfTs = orderTracks[ts]
      const isCartIdPresentInTracks = trackDetectsOfTs.find(
        (trackDetect: any) => trackDetect?.track_id === cartId
      )
      if (isCartIdPresentInTracks) {
        return ts
      }
    }
  }
  return null
}

export const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)

export const isValidLoyaltyId = (id: string) => /^[a-zA-Z0-9_-]+$/.test(id)
